import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Button from '@components/Button';
import { Input } from '@components/Input';
import { MultiSelect } from '@components/MultiSelect';
import { Switch } from '@components/Switch';
import Autocomplete from '@components/Autocomplete';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { knowledgeBaseService } from '@services';
import { alertActions, agentsActions, knowledgeBaseActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { formatVideoSize } from '@helpers/upload-file';
import {
    DOCUMENT_EXTENSIONS,
    DOCUMENT_LIMITS,
    agentAtLimit,
    documentFileError,
    isWebsiteAddress,
} from '@helpers/knowledge-base';
import { KNOWLEDGE_BASE_LIMIT_TOOLTIPS, KnowledgeBaseType, KnowledgeDocumentStatus } from '@constants/knowledge-base';
import DocumentStatusChip from '../DocumentStatusChip';
import { Modal, Content, DropZone, FileRow, FileDetails } from './styles';

export const FORM_TYPE = {
    NEW: 'NEW',
    EDIT: 'EDIT',
    EDIT_COPY: 'EDIT_COPY',
    DELETE: 'DELETE',
    VIEW: 'VIEW',
};

const NEW_CONTENT_TITLES = {
    [KnowledgeBaseType.FAQ]: 'KnowledgeBase.addFaq',
    [KnowledgeBaseType.DOCUMENT]: 'KnowledgeBase.addDocument',
    [KnowledgeBaseType.WEBSITE]: 'KnowledgeBase.addWebsite',
};

const documentForm = ({ id, title, agents, locations, file }) => {
    const form = new FormData();
    if (id) form.append('id', id);
    form.append('title', title);
    agents.forEach(agent => form.append('agents', agent));
    locations.forEach(location => form.append('locations', location));
    if (file) form.append('file', file);
    return form;
};

const DocumentFileField = ({ document, file, onPick, disabled }) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const name = file?.name ?? document?.fileName;
    const status = file ? null : document?.status;
    const openPicker = () => !disabled && inputRef.current?.click();

    const picker = (
        <input
            ref={inputRef}
            type="file"
            hidden
            accept={DOCUMENT_EXTENSIONS.join(',')}
            onChange={event => {
                onPick(event.target.files[0]);
                event.target.value = '';
            }}
        />
    );

    const dropHandlers = {
        onDragOver: event => {
            event.preventDefault();
            setIsDragOver(true);
        },
        onDragLeave: () => setIsDragOver(false),
        onDrop: event => {
            event.preventDefault();
            setIsDragOver(false);
            if (!disabled) onPick(event.dataTransfer.files[0]);
        },
    };

    if (!name) {
        return (
            <>
                <DropZone isDragOver={isDragOver} onClick={openPicker} {...dropHandlers}>
                    <UploadFileRoundedIcon color="action" />
                    <Typography variant="body2" color="textPrimary">
                        {t('KnowledgeBase.dropFileOr')}{' '}
                        <span style={{ color: 'var(--primary-main)', fontWeight: 500, textDecoration: 'underline' }}>
                            {t('KnowledgeBase.browse')}
                        </span>
                    </Typography>
                    <Typography variant="caption" color="textSecondary" align="center">
                        {t('KnowledgeBase.acceptedFiles', DOCUMENT_LIMITS)}
                    </Typography>
                </DropZone>
                {picker}
            </>
        );
    }

    return (
        <div>
            <FileRow {...dropHandlers}>
                <InsertDriveFileOutlinedIcon color="action" />
                <FileDetails>
                    <Typography variant="subtitle2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {formatVideoSize(file?.size ?? document.fileSize)}
                    </Typography>
                </FileDetails>
                {status && <DocumentStatusChip status={status} />}
                <Button variant="outlined" onClick={openPicker} disabled={disabled}>
                    {t('buttons.replace')}
                </Button>
                {picker}
            </FileRow>
            {status === KnowledgeDocumentStatus.FAILED && (
                <Typography
                    variant="caption"
                    sx={{ display: 'block', margin: '4px 12px 0', color: 'var(--error-main)' }}
                >
                    {t('KnowledgeBase.processingFailed')}
                </Typography>
            )}
        </div>
    );
};

const EditModal = ({
    selectedContent,
    setSelectedContent,
    currentQuery,
    modal,
    setModal,
    page,
    setPage,
    rowsPerPage,
    setDeleteModalOpen,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const agents = useSelector(state => state.agents.data);
    const { getLocationsOptions, mapSelectedLocationsToFormValue, hasSingleLocation } = useLocations();

    const contentType = modal.type === FORM_TYPE.NEW ? modal.contentType : selectedContent.type;
    const isDocument = contentType === KnowledgeBaseType.DOCUMENT;
    const isWebsite = contentType === KnowledgeBaseType.WEBSITE;
    const isFaq = contentType === KnowledgeBaseType.FAQ;
    const isExisting = modal.type === FORM_TYPE.EDIT || modal.type === FORM_TYPE.VIEW;

    const KnowledgeBaseSchema = yup.object().shape({
        title: yup.string().required('validation.required'),
        agents: yup.array().min(1, 'validation.required'),
        locations: yup.array().when('limitToLocations', {
            is: limitToLocations => limitToLocations,
            then: schema => schema.min(1, 'validation.required'),
        }),
        ...(isFaq && {
            question: yup.string().required('validation.required'),
            answer: yup.string().required('validation.required'),
        }),
        ...(isWebsite && {
            website: yup
                .string()
                .required('validation.required')
                .test('website', 'KnowledgeBase.invalidWebsite', value => !value || isWebsiteAddress(value)),
        }),
        ...(isDocument && !isExisting && { file: yup.mixed().required('validation.required') }),
    });

    const resolver = useYupValidationResolver(KnowledgeBaseSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            agents: [],
            locations: [],
            limitToLocations: false,
            question: '',
            answer: '',
            website: '',
            file: null,
        },
    });

    const limitToLocations = watch('limitToLocations');
    const selectedAgents = watch('agents');
    const file = watch('file');

    const agentsOptions = useMemo(() => {
        const counted = modal.type === FORM_TYPE.EDIT ? (selectedContent.agents ?? []) : [];
        const limitTooltip = KNOWLEDGE_BASE_LIMIT_TOOLTIPS[contentType];
        return agents.map(agent => {
            const disabled =
                agentAtLimit(agent, contentType) && !counted.includes(agent.id) && !selectedAgents.includes(agent.id);
            return {
                value: agent.id,
                name: agent.name,
                disabled,
                label: disabled ? (
                    <Tooltip disableInteractive title={t(limitTooltip)} placement="top">
                        <span>{agent.name}</span>
                    </Tooltip>
                ) : (
                    agent.name
                ),
            };
        });
    }, [agents, contentType, modal.type, selectedContent, selectedAgents, t]);

    const locationsOptions = getLocationsOptions();

    useEffect(() => {
        if (modal.type !== FORM_TYPE.NEW) {
            setValue('title', selectedContent.title, { shouldValidate: true });
            setValue('question', selectedContent.question ?? '', { shouldValidate: true });
            setValue('answer', selectedContent.answer ?? '', { shouldValidate: true });
            setValue('website', selectedContent.website ?? '', { shouldValidate: true });
            setValue('agents', selectedContent.agents ?? [], { shouldValidate: true });
            setValue('locations', hasSingleLocation ? [] : (selectedContent.locations ?? []), {
                shouldValidate: true,
            });
            setValue('limitToLocations', !hasSingleLocation && (selectedContent.locations ?? []).length > 0);
        }
    }, [selectedContent, modal.type, setValue, hasSingleLocation]);

    const onClose = () => {
        setSelectedContent(null);
        setModal({ open: false, type: FORM_TYPE.NEW });
    };

    const pickFile = picked => {
        if (!picked) return;
        const error = documentFileError(picked);
        if (error) {
            dispatch(alertActions.error(t(error.key, error.params)));
            return;
        }
        setValue('file', picked, { shouldValidate: true, shouldDirty: true });
    };

    const refetchFirstPage = async () => {
        if (page === 0) {
            await dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage));
        } else {
            setPage(0); // INFO: Triggers refetch of templates
        }
    };

    const REFETCH_MAP = {
        [FORM_TYPE.EDIT]: async () => await dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage)),
        [FORM_TYPE.NEW]: () => refetchFirstPage(),
        [FORM_TYPE.EDIT_COPY]: () => refetchFirstPage(),
    };

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [FORM_TYPE.NEW]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
        [FORM_TYPE.EDIT_COPY]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const { limitToLocations, locations, file: picked, ...values } = getValues();
        const scopedLocations = !hasSingleLocation && limitToLocations ? locations : [];
        const id = modal.type === FORM_TYPE.EDIT ? selectedContent.id : undefined;
        try {
            if (isDocument) {
                await knowledgeBaseService.saveDocument(
                    id,
                    documentForm({
                        id,
                        title: values.title,
                        agents: values.agents,
                        locations: scopedLocations,
                        file: picked,
                    }),
                );
            } else {
                await knowledgeBaseService.addOrUpdateKnowledgeBase({
                    ...values,
                    locations: scopedLocations,
                    type: contentType,
                    ...(id && { id }),
                });
            }
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error, DOCUMENT_LIMITS);
            return;
        }

        dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
        dispatch(agentsActions.get());
        REFETCH_MAP[modal.type]();
        onClose();
    };

    const isView = modal.type === FORM_TYPE.VIEW;
    const isReadyDocument =
        isDocument && !file && isExisting && selectedContent.status === KnowledgeDocumentStatus.READY;

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={isExisting ? t('KnowledgeBase.editContent') : t(NEW_CONTENT_TITLES[contentType])}
            onPrimaryAction={isView ? null : handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onAdditionalAction={selectedContent?.id && !isView ? () => setDeleteModalOpen(true) : null}
            additionalTextButton={t('buttons.delete')}
            additionalActionDisabled={isSubmitting}
        >
            <Content>
                <Controller
                    control={control}
                    name="title"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.title')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isView}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="agents"
                    render={({ field, fieldState: { error }, ref }) => (
                        <MultiSelect
                            {...field}
                            ref={ref}
                            onChange={value => {
                                setValue('agents', value, { shouldValidate: true, shouldDirty: true });
                            }}
                            options={agentsOptions}
                            label={t('KnowledgeBase.agents')}
                            fullWidth
                            countable
                            nameBased
                            required
                            disabled={isView}
                            error={error}
                        />
                    )}
                />
                {isFaq && (
                    <>
                        <Controller
                            control={control}
                            name="question"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('common.question')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                    disabled={isView}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="answer"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('common.answer')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    multiline
                                    required
                                    minRows={4}
                                    disabled={isView}
                                />
                            )}
                        />
                    </>
                )}
                {isWebsite && (
                    <Controller
                        control={control}
                        name="website"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('KnowledgeBase.website')}
                                placeholder={t('KnowledgeBase.websitePlaceholder')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                                disabled={isView}
                            />
                        )}
                    />
                )}
                {isDocument && (
                    <DocumentFileField
                        document={isExisting ? selectedContent : null}
                        file={file}
                        onPick={pickFile}
                        disabled={isView}
                    />
                )}
                {isReadyDocument && (
                    <Input
                        inputProps={{ value: selectedContent.description ?? '' }}
                        label={t('common.description')}
                        fullWidth
                        multiline
                        minRows={4}
                        disabled
                    />
                )}
                {!hasSingleLocation && (
                    <Controller
                        control={control}
                        name="limitToLocations"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('limitToLocations')}
                                handleChange={event => {
                                    setValue('limitToLocations', event.target.checked, { shouldDirty: true });
                                    if (!event.target.checked) {
                                        setValue('locations', [], { shouldValidate: true, shouldDirty: true });
                                    }
                                }}
                                label={t('KnowledgeBase.limitToLocations')}
                                disabled={isView}
                            />
                        )}
                    />
                )}
                {!hasSingleLocation && limitToLocations && (
                    <Controller
                        control={control}
                        name="locations"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Autocomplete
                                ref={ref}
                                label={t('common.locations')}
                                options={locationsOptions}
                                value={mapSelectedLocationsToFormValue(field.value)}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                inputProps={{ style: { display: field.value?.length > 0 ? 'none' : 'block' } }}
                                onChangeAutoComplete={items => {
                                    setValue(
                                        'locations',
                                        items.map(item => item.id),
                                        { shouldValidate: true, shouldDirty: true },
                                    );
                                }}
                                groupMultiProps={{
                                    getOptionLabel: option => option.label,
                                }}
                                required
                                error={error}
                                disabled={isView}
                            />
                        )}
                    />
                )}
            </Content>
        </Modal>
    );
};

export default EditModal;
